﻿// ==============================
// File: src/pages/Shop.jsx
// Mudo Shop — Supabase-backed marketplace UI with:
// - Multi-image upload + cover & reorder
// - Reverse geocoded locations + autocomplete + near-me radius
// - Saved filters, favourites, report
// - Local in-app chat & offer flow with simulated/real "escrow"
// - Publish/Unpublish products to visitor marketplace
// - 5% hosting note
// ==============================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { stripePromise } from "../lib/stripe";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useActiveStudioId } from "../hooks/useActiveStudioId";
import * as productsApi from "../lib/mudoNetwork/productsApi";
import * as offersApi from "../lib/mudoNetwork/offersApi";
import * as messagesApi from "../lib/mudoNetwork/messagesApi";
import { useNotifications } from "../contexts/NotificationsContext";
import { useAuth } from "../contexts/AuthContext";
import { useUnreadCount } from "../contexts/UnreadCountContext";
import * as studiosApi from "../lib/mudoNetwork/studiosApi";
import { getSoldState } from "../utils/productSold";
import { supabase } from "../supabaseClient";
import InboxModal from "../components/Messages/InboxModal";
import ListingOffersModal from "../components/Shop/ListingOffersModal";
import { Mail } from "lucide-react";
import {
  getNominatimHeaders,
  applyNominatimContact,
} from "../utils/nominatim";
import {
  SHOP_STATUS_LABELS,
  SHOP_STATUSES,
  OFFER_STATUS_TO_SHOP_STATUS,
} from "../constants/shopStatus";
import { useShopDemoState } from "../hooks/useShopDemoState";
import OfferModal from "../components/Shop/OfferModal";
import { createCheckoutSession } from "../api/checkoutClient"; // ?? Stripe checkout helper
import { evaluateOffer } from "../ai/estimators/offerHelperEstimator";
import { OfferHelperPanel } from "../components/Shop/OfferHelperPanel";
import { estimateListingText } from "../ai/estimators/listingTextEstimator";
import { ListingAssistantPanel } from "../components/Shop/ListingAssistantPanel";
import { usePricingSettings } from "../contexts/PricingSettingsContext";
import useStripePayoutsGuard from "../hooks/useStripePayoutsGuard";
import Button from "../ui/Button.jsx";
import WidgetShell from "../ui/WidgetShell.jsx";
import PageLayout from "../ui/PageLayout.jsx";
import PageHeader from "../ui/PageHeader.jsx";
import FileUpload from "../ui/FileUpload.jsx";
import { apiFetch } from "../lib/apiFetch";
import ShopPayNowModal from "../components/ShopPayNowModal.jsx";
import ImageCarousel from "../components/Shop/ImageCarousel";
import { uploadProductImages } from "../lib/storage/uploadProductImages";

// Read ESCROW_URL from env (still used by OfferDrawer’s escrow prototype)
const ESCROW_URL =
  import.meta.env.VITE_ESCROW_URL?.trim() ||
  "https://jjgnmkzfspuxbifsrgld.functions.supabase.co/escrow2";

function fireToast(message, tone = "info", ttl = 2200) {
  try {
    window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { message, tone, ttl } }));
  } catch {}
}

/* ---------- Local Storage Keys ---------- */
const LS_LISTINGS = "mudoShopListings";
const LS_FAVS = "mudoShopFavourites";
const LS_MYID = "mudoShopMyId";
const LS_MY_POS = "mudoShopMyPosition"; // { lat, lng, label }
const LS_CHATS = "mudoShopChats"; // { [listingId]: [{id, from, text, ts}] }
const LS_OFFERS = "mudoShopOffers"; // { [listingId]: [{id, from, amount, note, status, ts, clientSecret?}] }
const LS_FILTERS = "mudoShopSavedFilters";

const NOM_BASE = "https://nominatim.openstreetmap.org";

/* ---------- UI tokens (legacy objects still used in drawers/modals) ---------- */
const shell = {
  padding: 16,
  color: "#fff",
  minHeight: "100vh",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const h1 = { margin: 0, fontSize: 22, fontWeight: 800 };
const subtitle = { margin: 0, opacity: 0.8, fontSize: 12, lineHeight: 1.4 };
const note = { marginTop: 4, opacity: 0.7, fontSize: 11 };
const input = {
  padding: "8px 10px",
  minWidth: 200,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  outline: "none",
  fontSize: 13,
};
const smallInput = { ...input, minWidth: 110 };
const select = {
  ...input,
  minWidth: 160,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  colorScheme: "dark",
};
const btn = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  fontSize: 13,
  cursor: "pointer",
};
const primaryBtn = {
  ...btn,
  background: "rgba(37,99,235,0.22)",
  borderColor: "rgba(37,99,235,0.35)",
};
const warnBtn = {
  ...btn,
  background: "rgba(251,191,36,0.25)",
  borderColor: "rgba(251,191,36,0.35)",
};
const dangerBtn = {
  ...btn,
  background: "rgba(239,68,68,0.18)",
  borderColor: "rgba(239,68,68,0.45)",
};
const grid = {
  display: "grid",
  gap: 12,
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
};
const card = {
  padding: 12,
  borderRadius: 14,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const title = { margin: 0, fontWeight: 800, fontSize: 16 };
const meta = { margin: 0, opacity: 0.85, fontSize: 12, lineHeight: 1.4 };
const help = { fontSize: 11, opacity: 0.7, marginTop: 4 };
const price = { fontWeight: 800, fontSize: 16 };

/* ---------- helpers ---------- */
const readLS = (k, f) => {
  try {
    const r = localStorage.getItem(k);
    return r ? JSON.parse(r) : f;
  } catch {
    return f;
  }
};
const writeLS = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};
const newid = () =>
  globalThis.crypto?.randomUUID
    ? crypto.randomUUID()
    : `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const myId = () => {
  let me = readLS(LS_MYID, null);
  if (!me) {
    me = newid();
    writeLS(LS_MYID, me);
  }
  return me;
};
const kmBetween = (a, b) => {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null)
    return Infinity;
  const R = 6371,
    dLat = ((b.lat - a.lat) * Math.PI) / 180,
    dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 =
    Math.cos((a.lat * Math.PI) / 180) *
    Math.cos((b.lat * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1 + s2));
};
const labelFromNominatim = (addr = {}, fb = "") => {
  const parts = [
    addr.suburb || addr.neighbourhood || addr.village || addr.locality,
    addr.city || addr.town || addr.county,
    addr.state || addr.region,
    addr.country_code ? addr.country_code.toUpperCase() : addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : fb;
};
async function reverseGeocode(lat, lng) {
  try {
    const url = applyNominatimContact(new URL(`${NOM_BASE}/reverse`));
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("lat", lat);
    url.searchParams.set("lon", lng);
    url.searchParams.set("addressdetails", "1");
    const headers = { Accept: "application/json", ...getNominatimHeaders() };
    const res = await fetch(url.toString(), { headers });
    const json = await res.json();
    return {
      label: labelFromNominatim(json.address || {}, `${lat.toFixed(4)}, ${lng.toFixed(4)}`),
    };
  } catch {
    return { label: `${lat.toFixed(4)}, ${lng.toFixed(4)}` };
  }
}
async function forwardGeocode(q, limit = 6) {
  try {
    const url = applyNominatimContact(new URL(`${NOM_BASE}/search`));
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("q", q);
    url.searchParams.set("addressdetails", "1");
    url.searchParams.set("limit", String(limit));
    const headers = { Accept: "application/json", ...getNominatimHeaders() };
    const res = await fetch(url.toString(), { headers });
    const arr = await res.json();
    return (Array.isArray(arr) ? arr : []).map((r) => ({
      lat: +r.lat,
      lng: +r.lon,
      label: labelFromNominatim(r.address || {}, r.display_name || ""),
    }));
  } catch {
    return [];
  }
}

// Validate UUID v4-ish (covers v1-5) to avoid 22P02 errors from Supabase when studioId is malformed
function looksLikeUuid(s) {
  if (typeof s !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s.trim()
  );
}

function resolveOfferListingId(offer) {
  if (!offer) return null;
  return (
    offer.listing_product_id ||
    offer?.listing?.product_id ||
    offer.product_id ||
    offer.listing_id ||
    null
  );
}

/* ---------- Multi-image utils ---------- */
function moveIndex(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/* ---------- Chat & Offers drawers ---------- */
function ChatDrawer({ open, onClose, listingId, listingTitle, meId, onNotify }) {
  const [chats, setChats] = useState(() => readLS(LS_CHATS, {}));
  const [text, setText] = useState("");
  useEffect(() => writeLS(LS_CHATS, chats), [chats]);
  if (!open) return null;
  const items = chats[listingId] || [];
  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 2100,
        }}
        onClick={onClose}
      />
      <aside
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 360,
          background: "rgba(22,22,24,0.98)",
          borderLeft: "1px solid rgba(255,255,255,0.12)",
          zIndex: 2101,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            fontWeight: 800,
          }}
        >
          Chat – {listingTitle}
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {items.length === 0 && (
            <div style={{ opacity: 0.7, fontSize: 12 }}>Start the conversation.</div>
          )}
          {items.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.from === meId ? "flex-end" : "flex-start",
                maxWidth: "80%",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                padding: "6px 8px",
                borderRadius: 10,
                fontSize: 13,
              }}
            >
              <div
                style={{
                  opacity: 0.7,
                  fontSize: 11,
                  marginBottom: 2,
                }}
              >
                {m.from === meId ? "You" : "Seller"}
              </div>
              {m.text}
            </div>
          ))}
        </div>
        <div
          style={{
            padding: 12,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
          }}
        >
          <input
            style={{ ...input, minWidth: 0 }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a message…"
            onKeyDown={(e) => {
              if (e.key === "Enter") send();
            }}
          />
          <button style={primaryBtn} onClick={send} disabled={!text.trim()}>
            Send
          </button>
        </div>
      </aside>
    </>
  );
  function send() {
    const t = text.trim();
    if (!t) return;
    setChats((prev) => {
      const next = { ...prev };
      const arr = next[listingId] ? next[listingId].slice() : [];
      arr.push({ id: newid(), from: meId, text: t, ts: Date.now() });
      next[listingId] = arr;
      return next;
    });
    setText("");
    // TODO(server): persist message, deliver push, spam checks
    onNotify?.({
      world: "both",
      type: "shop-message",
      title: listingTitle ? `Chat on ${listingTitle}` : "New shop message",
      body: t,
      target: {
        entity: "shop",
        entityId: listingId,
        route: "/shop",
      },
      severity: "info",
      meta: { listingId },
    });
  }
}
function OfferDrawer({
  open,
  onClose,
  listingId,
  listingTitle,
  listing,
  mine,
  meId,
  pricingSettings,
}) {
  const [offers, setOffers] = useState(() => readLS(LS_OFFERS, {}));
  const [amount, setAmount] = useState("");
  const [noteText, setNoteText] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [aiInsertNotice, setAiInsertNotice] = useState("");

  useEffect(() => writeLS(LS_OFFERS, offers), [offers]);

  if (!open) return null;
  const items = offers[listingId] || []; // {id, from, amount, note, status, ts, clientSecret?}

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 2100,
        }}
        onClick={onClose}
      />
      <aside
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 380,
          background: "rgba(22,22,24,0.98)",
          borderLeft: "1px solid rgba(255,255,255,0.12)",
          zIndex: 2101,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            fontWeight: 800,
          }}
        >
          Offers – {listingTitle}
        </div>

        {!mine && (
          <div
            style={{
              padding: 12,
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.8 }}>Make an offer</div>
            <input
              type="number"
              min="1"
              step="1"
              style={input}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Your offer (AUD)"
            />
            <textarea
              style={{ ...input, minHeight: 80 }}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Optional note…"
            />
            {aiInsertNotice && (
              <div style={{ fontSize: 11, opacity: 0.7 }}>{aiInsertNotice}</div>
            )}
            <button
              style={primaryBtn}
              onClick={createOffer}
              disabled={!(Number(amount) >= 1)}
            >
              Send offer
            </button>
            <div style={help}>
              Your offer is private to the seller. If accepted, payment moves to a secure escrow
              (5% Mudo fee).
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {errorMsg && (
            <div
              style={{
                padding: 8,
                borderRadius: 8,
                background: "rgba(239,68,68,0.18)",
                border: "1px solid rgba(239,68,68,0.45)",
                fontSize: 12,
              }}
            >
              {errorMsg}
            </div>
          )}

          {items.length === 0 && (
            <div style={{ opacity: 0.7, fontSize: 12 }}>No offers yet.</div>
          )}

          {items
            .slice()
            .reverse()
            .map((o) =>
              (() => {
                const evalData = evaluateOffer(
                  listing || {},
                  Number(o.amount || 0),
                  {
                    targetMargin: pricingSettings?.targetWholesaleMargin ?? 0.5,
                    minHourly: pricingSettings?.minHourlyRate ?? listing?.minHourly ?? null,
                  }
                );

                return (
                  <div
                    key={o.id}
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>
                        ${Number(o.amount || 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>
                        {busyId === o.id ? "Pending" : null} {o.status}
                      </div>
                    </div>
                    {o.note && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>{o.note}</div>
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        opacity: 0.7,
                        marginTop: 6,
                      }}
                    >
                      {o.from === meId ? "You" : "Buyer"} •{" "}
                      {new Date(o.ts).toLocaleString()}
                    </div>

                    {o.clientSecret && (
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.85,
                          marginTop: 6,
                        }}
                      >
                        <b>Payment Intent ready.</b> (client secret stored)
                      </div>
                    )}

                    {mine && (
                      <div className="mt-3">
                        <OfferHelperPanel
                          evalData={evalData}
                          onSelectCounter={(amt) => setAmount(String(amt))}
                          product={listing}
                          onUseAiMessage={(msg) => {
                            if (!msg) return;
                            setNoteText(msg);
                            setAiInsertNotice("AI message added. Edit then press Send.");
                          }}
                        />
                      </div>
                    )}

                    {mine && (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        {o.status === "pending" && (
                          <>
                            <button
                              style={primaryBtn}
                              onClick={() => setStatus(o.id, "accepted")}
                            >
                              Accept
                            </button>
                            <button
                              style={btn}
                              onClick={() => setStatus(o.id, "declined")}
                            >
                              Decline
                            </button>
                          </>
                        )}
                        {o.status === "accepted" && (
                          <button
                            style={primaryBtn}
                            disabled={busyId === o.id}
                            onClick={() => createEscrowForOffer(o)}
                            title="Creates PaymentIntent via Supabase Edge Function"
                          >
                            {busyId === o.id ? "Creating escrow…" : "Create escrow"}
                          </button>
                        )}
                        {o.status === "escrow" && (
                          <button
                            style={primaryBtn}
                            onClick={() => setStatus(o.id, "completed")}
                          >
                            Mark completed
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
        </div>
      </aside>
    </>
  );

  function createOffer() {
    const n = Number(amount || 0);
    if (!Number.isFinite(n) || n < 1) return;
    setOffers((prev) => {
      const next = { ...prev };
      const arr = next[listingId] ? next[listingId].slice() : [];
      arr.push({
        id: newid(),
        from: meId,
        amount: n,
        note: noteText.trim(),
        status: "pending",
        ts: Date.now(),
      });
      next[listingId] = arr;
      return next;
    });
    setAmount("");
    setNoteText("");
    // TODO(server): write offer row, notify seller
  }

  function setStatus(id, status, extra = {}) {
    setOffers((prev) => {
      const next = { ...prev };
      next[listingId] = (next[listingId] || []).map((o) =>
        o.id === id ? { ...o, status, ...extra } : o
      );
      return next;
    });
  }

  async function createEscrowForOffer(offer) {
    setErrorMsg("");
    setBusyId(offer.id);
    try {
      const amount_cents = Math.round(Number(offer.amount || 0) * 100);
      if (!Number.isFinite(amount_cents) || amount_cents <= 0) {
        throw new Error("Invalid offer amount.");
      }
      const seller_stripe_id = ""; // TODO: fill when you onboard sellers

      const res = await fetch(ESCROW_URL || "", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer_id: offer.id,
          amount_cents,
          seller_stripe_id,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(json?.error || `Escrow function failed (${res.status})`);

      const clientSecret = json?.clientSecret;
      setStatus(offer.id, "escrow", clientSecret ? { clientSecret } : {});
    } catch (err) {
      console.error(err);
      setErrorMsg(String(err.message || err));
    } finally {
      setBusyId(null);
    }
  }
}
/* ---------- Listing Modal (create/edit) ---------- */
function ListingModal({ open, onClose, onSave, initial }) {
  const fresh = {
    title: "",
    category: "Equipment",
    condition: "Used - Good",
    price: "",
    location: "",
    lat: undefined,
    lng: undefined,
    description: "",
    images: [],
    imageFiles: [],
    email: "",
    tags: [],
    shortDescription: "",
  };

  const [form, setForm] = useState(() => initial || fresh);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [locQuery, setLocQuery] = useState("");
  const [locOpts, setLocOpts] = useState([]);
  const locTimer = useRef(null);
  const lastQueryRef = useRef("");
  const fileRef = useRef(null);
  const aliveRef = useRef(true);
  const listingSuggestions = useMemo(() => {
    const helperDraft = {
      name: form.title || form.name,
      category: form.category,
      clayBody: form.clayBody,
      glazeName: form.glazeName,
      surfaceFinish: form.surfaceFinish,
      sizeLabel: form.sizeLabel,
      capacityMl: form.capacityMl,
      intendedUse: form.intendedUse,
      styleTone: form.styleTone,
      studioName: "",
    };
    return estimateListingText(helperDraft);
  }, [form]);

  function handleApplyListingSuggestions(payload) {
    setForm((prev) => {
      const next = { ...prev };
      if (payload.title) next.title = payload.title;
      if (payload.shortDescription) next.shortDescription = payload.shortDescription;
      if (payload.longDescription) next.description = payload.longDescription;
      if (payload.tags && Array.isArray(payload.tags)) next.tags = payload.tags;
      return next;
    });
  }

  useEffect(() => {
    const base = initial || fresh;
    const images = Array.isArray(base.images) ? base.images : [];
    const imageFiles = Array.isArray(base.imageFiles)
      ? base.imageFiles
      : new Array(images.length).fill(null);
    setForm({ ...fresh, ...base, images, imageFiles });
    setErrors({});
    setLocQuery("");
    setLocOpts([]);
    setIsSaving(false);
    setSaveStatus("");
  }, [initial]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!locQuery || locQuery.length < 3) {
      setLocOpts([]);
      return;
    }
    clearTimeout(locTimer.current);
    const q = locQuery;
    lastQueryRef.current = q;
    locTimer.current = setTimeout(async () => {
      const results = await forwardGeocode(q, 6);
      // Avoid race: only set results for the latest query
      if (lastQueryRef.current === q) setLocOpts(results);
    }, 280);
    return () => clearTimeout(locTimer.current);
  }, [locQuery]);

  if (!open) return null;

  const field = (k, props = {}) => (
    <input
      {...props}
      style={{ ...input, width: "100%" }}
      value={form[k] ?? ""}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
    />
  );

  function validate() {
    const e = {};
    if (!String(form.title).trim()) e.title = "Title is required.";
    const nPrice = Number(form.price);
    if (!Number.isFinite(nPrice) || nPrice < 0)
      e.price = "Price must be a number = 0.";
    const effectiveLocation =
      String(form.location || "").trim() || String(locQuery || "").trim();
    if (!effectiveLocation) e.location = "Location is required.";
    if (!String(form.location || "").trim() && String(locQuery || "").trim()) {
      setForm((s) => ({ ...s, location: String(locQuery || "").trim() }));
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function useMyLocation() {
    if (!("geolocation" in navigator)) {
      alert("Geolocation not available.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords || {};
        const rev = await reverseGeocode(lat, lng);
        setForm((s) => ({
          ...s,
          location: rev.label,
          lat,
          lng,
        }));
      },
      (err) => {
        console.warn(err);
        alert("Couldn't get your location. Check permissions.");
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 600000,
      }
    );
  }

  async function onFiles(filesOrEvent) {
    let files = [];
    if (filesOrEvent?.target?.files) {
      files = Array.from(filesOrEvent.target.files || []);
    } else if (filesOrEvent instanceof FileList) {
      files = Array.from(filesOrEvent);
    } else if (Array.isArray(filesOrEvent)) {
      files = filesOrEvent;
    }

    if (!files.length) return;

    const safeFiles = files.filter((f) => f instanceof File);
    if (!safeFiles.length) return;

    const reads = await Promise.all(
      safeFiles.map(
        (f) =>
          new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(String(r.result || ""));
            r.readAsDataURL(f);
          })
      )
    );
    setForm((s) => ({
      ...s,
      images: [...(s.images || []), ...reads],
      imageFiles: [...(s.imageFiles || []), ...safeFiles],
    }));
  }

  const cover = form.images?.[0];
  const hasImages = Array.isArray(form.images) && form.images.length > 0;

  return (
    <>
      <style>{`
        .mudo-dark-select, .mudo-dark-select option { background:#1e1f22 !important; color:#fff !important; }
        @keyframes mudoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .mudo-spinner { width: 14px; height: 14px; border-radius: 9999px; border: 2px solid rgba(255,255,255,0.35); border-top-color: rgba(255,255,255,0.95); animation: mudoSpin 0.9s linear infinite; }
      `}</style>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 2000,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed",
          top: "6%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(860px, 96vw)",
          maxHeight: "92vh",
          overflowY: "auto",
          background: "rgba(22,22,24,0.98)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 14,
          padding: 16,
          zIndex: 2001,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800 }}>
            {initial ? "Edit listing" : "New listing"}
          </div>
          <button style={btn} onClick={onClose}>
            Close
          </button>
        </div>

        {cover && (
          <div
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              marginBottom: 10,
            }}
          >
            <img
              src={cover}
              alt="Cover"
              style={{
                width: "100%",
                maxHeight: 280,
                objectFit: "cover",
              }}
              loading="lazy"
            />
          </div>
        )}

        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Title</label>
            {field("title", {
              placeholder: "e.g. Skutt KM-1027 Kiln",
            })}
            {errors.title && (
              <div
                style={{
                  ...help,
                  color: "#ffb4b4",
                }}
              >
                {errors.title}
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Category</label>
            <select
              className="mudo-dark-select"
              style={{ ...select, width: "100%" }}
              value={form.category}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value,
                })
              }
            >
              {[
                "Equipment",
                "Kilns",
                "Wheels",
                "Tools",
                "Materials",
                "Moulds",
                "Furniture",
                "Other",
              ].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Condition</label>
            <select
              className="mudo-dark-select"
              style={{ ...select, width: "100%" }}
              value={form.condition}
              onChange={(e) =>
                setForm({
                  ...form,
                  condition: e.target.value,
                })
              }
            >
              {[
                "New",
                "New - Open Box",
                "Used - Excellent",
                "Used - Good",
                "Used - Fair",
                "For parts",
              ].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Price (AUD)</label>
            {field("price", {
              type: "number",
              min: 0,
              step: 1,
              placeholder: "0",
              inputMode: "numeric",
            })}
            <div style={help}>Enter a whole number in AUD (e.g. 1450).</div>
            {errors.price && (
              <div
                style={{
                  ...help,
                  color: "#ffb4b4",
                }}
              >
                {errors.price}
              </div>
            )}
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Location</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 8,
              }}
            >
              {field("location", {
                placeholder: "Suburb, City, State",
              })}
              <button style={btn} onClick={useMyLocation}>
                Use my location
              </button>
            </div>
            {errors.location && (
              <div
                style={{
                  ...help,
                  color: "#ffb4b4",
                }}
              >
                {errors.location}
              </div>
            )}
            <div style={help}>
              Type to search or use your current location. We’ll store suburb/city and approx.
              coordinates.
            </div>

            {/* Autocomplete */}
            <div style={{ position: "relative" }}>
              <input
                style={{ ...input, width: "100%", marginTop: 8 }}
                placeholder="Search location (autocomplete)…"
                value={locQuery}
                onChange={(e) => setLocQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();

                  const first = Array.isArray(locOpts) && locOpts.length ? locOpts[0] : null;
                  if (first) {
                    setForm((s) => ({
                      ...s,
                      location: first.label,
                      lat: first.lat,
                      lng: first.lng,
                    }));
                    setLocOpts([]);
                    setLocQuery("");
                    return;
                  }

                  const q = String(locQuery || "").trim();
                  if (!q) return;
                  setForm((s) => ({ ...s, location: q }));
                  setLocOpts([]);
                  setLocQuery("");
                }}
              />
              {locOpts.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    right: 0,
                    background: "rgba(25,25,25,0.98)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    maxHeight: 220,
                    overflowY: "auto",
                    zIndex: 10,
                  }}
                >
                  {locOpts.map((o, i) => (
                    <div
                      key={`${o.lat},${o.lng},${i}`}
                      role="button"
                      onMouseDown={() => {
                        setForm((s) => ({
                          ...s,
                          location: o.label,
                          lat: o.lat,
                          lng: o.lng,
                        }));
                        setLocOpts([]);
                        setLocQuery("");
                      }}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(255,255,255,0.06)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      {o.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Contact email</label>
            {field("email", {
              type: "email",
              placeholder: "you@example.com",
              inputMode: "email",
            })}
            <div style={help}>
              Buyers can contact you directly (until in-app messaging is server-backed).
            </div>
          </div>

          {/* Multi-image uploader */}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Photos</label>
            <div className="mt-1">
              <FileUpload
                accept="image/*"
                multiple
                onFilesSelected={onFiles}
                label="Upload photos"
                className="w-full max-w-xs"
              />
            </div>
            <div style={help}>
              Upload multiple photos. First photo is the cover. Drag isn’t supported yet; use
              arrows.
            </div>

            {/* Thumbs */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              {Array.isArray(form.images) &&
                form.images.map((src, i) => {
                  const totalImages = form.images.length;
                  return (
                    <div key={i} className="relative">
                      {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                      <img
                        src={src}
                        alt="photo"
                        className="w-24 h-24 object-cover rounded-lg border border-[var(--mudo-border)]"
                        loading="lazy"
                      />
                      <div className="absolute top-1 left-1 flex gap-1">
                        <Button
                          variant="soft"
                          className="px-2 py-1 text-xs"
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              images: moveIndex(s.images, i, -1),
                              imageFiles: moveIndex(
                                Array.isArray(s.imageFiles)
                                  ? s.imageFiles
                                  : new Array((s.images || []).length).fill(null),
                                i,
                                -1
                              ),
                            }))
                          }
                          title="Move left"
                          disabled={i === 0}
                        >
                          Left
                        </Button>
                        <Button
                          variant="soft"
                          className="px-2 py-1 text-xs"
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              images: moveIndex(s.images, i, +1),
                              imageFiles: moveIndex(
                                Array.isArray(s.imageFiles)
                                  ? s.imageFiles
                                  : new Array((s.images || []).length).fill(null),
                                i,
                                +1
                              ),
                            }))
                          }
                          title="Move right"
                          disabled={i === totalImages - 1}
                        >
                          Right
                        </Button>
                      </div>
                      <Button
                        variant="danger"
                        className="absolute bottom-1 right-1 px-2 py-1 text-xs"
                        onClick={() =>
                          setForm((s) => ({
                            ...s,
                            images: s.images.filter((_x, j) => j !== i),
                            imageFiles: (Array.isArray(s.imageFiles)
                              ? s.imageFiles
                              : new Array((s.images || []).length).fill(null)
                            ).filter((_x, j) => j !== i),
                          }))
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  );
                })}
            </div>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Description</label>
            <textarea
              style={{
                ...input,
                width: "100%",
                minHeight: 90,
                resize: "vertical",
              }}
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
              placeholder="Condition, inclusions, pickup/delivery, etc."
            />
          </div>
        </div>

        <div className="mt-4">
          <ListingAssistantPanel
            suggestions={listingSuggestions}
            onApply={handleApplyListingSuggestions}
            disabled={false}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 12,
          }}
        >
          <button style={btn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={primaryBtn}
            type="button"
            disabled={isSaving}
            onClick={async () => {
              if (isSaving) return;
              if (!validate()) return;
              setIsSaving(true);
              setSaveStatus(hasImages ? `Uploading ${form.images.length} photo${form.images.length === 1 ? "" : "s"}…` : "Saving…");
              try {
                await onSave?.({
                  ...form,
                  price: Number(form.price || 0),
                  location:
                    String(form.location || "").trim() ||
                    String(locQuery || "").trim() ||
                    "",
                });
              } catch (e) {
                fireToast("Failed to save listing", "error");
                alert(e?.message || "Failed to save listing.");
              } finally {
                if (!aliveRef.current) return;
                setIsSaving(false);
                setSaveStatus("");
              }
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {isSaving && <span className="mudo-spinner" aria-hidden />}
              {isSaving ? (hasImages ? "Uploading images…" : "Saving…") : "Save listing"}
            </span>
          </button>
        </div>

        {isSaving && saveStatus && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85, textAlign: "right" }}>
            {saveStatus}
          </div>
        )}
      </div>
    </>
  );
}
/* ---------- Main Shop page ---------- */
export default function Shop() {
  const me = useMemo(myId, []);
  const { studioId, setStudioId } = useActiveStudioId();
  const { notifyAndInbox, addNotification, setOfferNotifications } = useNotifications() || {};
  const { userId, user, profile, studioProfile } = useAuth() || {};
  const { pricingSettings } = usePricingSettings();
  const { isStripeConnected, ensureStripeConnected } = useStripePayoutsGuard();
  const activeStudioId =
    studioId ||
    studioProfile?.studio_id ||
    profile?.studio_id ||
    null;
  const studioIdValid = useMemo(() => looksLikeUuid(studioId || ""), [studioId]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkOfferId = searchParams.get("offerId");
  const deepLinkThreadId = searchParams.get("threadId");
  const [pendingOfferId, setPendingOfferId] = useState(null);
  const [pendingThreadId, setPendingThreadId] = useState(null);

  // Track which listing is currently redirecting to Stripe
  const [paymentLoadingId, setPaymentLoadingId] = useState(null);

  // Try to discover the studio’s Stripe account ID
  const studioStripeAccountId = useMemo(() => {
    // Prefer studioProfile if available
    if (studioProfile?.stripe_account_id) return studioProfile.stripe_account_id;
    if (studioProfile?.connect_account_id) return studioProfile.connect_account_id;

    // Fallbacks on merged profile (if you stored it there)
    if (profile?.stripe_account_id) return profile.stripe_account_id;
    if (profile?.studio_stripe_account_id) return profile.studio_stripe_account_id;

    return null;
  }, [studioProfile, profile]);

  // filters (restore from LS)
  const saved = readLS(LS_FILTERS, null);
  const [query, setQuery] = useState(saved?.query ?? "");
  const [category, setCategory] = useState(saved?.category ?? "all");
  const [condition, setCondition] = useState(saved?.condition ?? "all");
  const [minPrice, setMinPrice] = useState(saved?.minPrice ?? "");
  const [maxPrice, setMaxPrice] = useState(saved?.maxPrice ?? "");
  const [nearMe, setNearMe] = useState(saved?.nearMe ?? false);
  const [radiusKm, setRadiusKm] = useState(saved?.radiusKm ?? 25);
  const [locQuery, setLocQuery] = useState(saved?.locQuery ?? "");
  const [locOpts, setLocOpts] = useState([]);
  const locTimer = useRef(null);
  const lastQueryRef = useRef("");
  const [onlyMine, setOnlyMine] = useState(saved?.onlyMine ?? false);
  const [onlyFavs, setOnlyFavs] = useState(saved?.onlyFavs ?? false);

  const [myPos, setMyPos] = useState(() => readLS(LS_MY_POS, null));
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [favs, setFavs] = useState(new Set(readLS(LS_FAVS, [])));

  // Stripe Pay Now modal state
  const [payModal, setPayModal] = useState({ open: false, clientSecret: null, returnTo: null });

  const [modalOpen, setModalOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatFor, setChatFor] = useState(null); // {id,title}

  const [offerOpen, setOfferOpen] = useState(false);
  const [offerFor, setOfferFor] = useState(null); // {id,title,mine}

  // Inbox state
  const [inboxOpen, setInboxOpen] = useState(false);
  const [initialThreadId, setInitialThreadId] = useState(null);
  const deepLinkThreadHandledRef = useRef(false);
  const { unreadCount } = useUnreadCount(); // Get unread count from Realtime subscription context
  const { getListingState, updateListingState } = useShopDemoState();
  const [offerModalFor, setOfferModalFor] = useState(null);
  const [offersByListing, setOffersByListing] = useState({});
  const [offersLoaded, setOffersLoaded] = useState(false);
  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [updatingOfferId, setUpdatingOfferId] = useState(null);
  const offersErrorLoggedRef = useRef(false);
  const deepLinkOfferHandledRef = useRef(false);
  const [offersError, setOffersError] = useState(null);
  const [offersReloadTick, setOffersReloadTick] = useState(0);
  const offersAbortRef = useRef(null);
  const offersInFlightRef = useRef(false);
  const offersRetryTimerRef = useRef(null);
  const offersAttemptRef = useRef(0);
  const lastFetchedStudioUserIdRef = useRef(null);
  const offersFailCountRef = useRef(0);
  const offersLastAttemptRef = useRef("");
  const offersAuthBlockedRef = useRef(false);
  const offersAuthNotifiedRef = useRef(false);
  const offersErrorMessage =
    typeof offersError === "string" ? offersError : offersError?.message;
  const offersErrorCode =
    typeof offersError === "string" ? null : offersError?.code;
  const offersAuthError =
    offersAuthBlockedRef.current ||
    offersErrorCode === "AUTH_REQUIRED" ||
    offersErrorCode === "401" ||
    offersErrorCode === "403";

  // Load products from Supabase on mount
  useEffect(() => {
    if (!studioId || !studioIdValid) {
      setLoading(false);
      return;
    }

    loadProducts();
  }, [studioId, studioIdValid, me]);

  const loadProducts = useCallback(async () => {
    if (!studioId || !studioIdValid) return;
    
    setLoading(true);
    setProductsError(null);
    
    try {
      const { data, error } = await productsApi.listAllProductsForStudio(studioId);

      if (error) {
        console.error("Error loading products:", error);
        
        // Detect network errors (QUIC protocol error, fetch failure, etc.)
        const errorMsg = String(error?.message || error || "");
        const isNetworkError = 
          errorMsg.toLowerCase().includes("err_quic_protocol_error") ||
          errorMsg.toLowerCase().includes("failed to fetch") ||
          errorMsg.toLowerCase().includes("network") ||
          error?.name === "TypeError";
        
        if (isNetworkError) {
          setProductsError({
            message: "Network issue. Please retry.",
            isNetworkError: true,
          });
        } else {
          setProductsError({
            message: "Failed to load products from database",
            isNetworkError: false,
          });
        }
      } else {
        // Convert Supabase format to local listing format
        const converted = (data || []).map((p) => ({
          id: p.id,
          title: p.title,
          description: p.description || "",
          price: Math.round((p.price_cents || 0) / 100), // Convert cents to dollars
          category: p.category || "", // Will be empty if migration not run
          condition: p.condition || "", // Will be empty if migration not run
          location: p.location || "", // Will be empty if migration not run
          lat: p.lat || null,
          lng: p.lng || null,
          images: p.image_gallery || [],
          status: p.is_active ? "Available" : "Sold",
          createdBy: me,
          createdAt: new Date(p.created_at).getTime(),
          isPublished: p.is_published, // Add publish status
          sold_at: p.sold_at || null,
        }));
        setListings(converted);
        setProductsError(null);
      }
    } catch (err) {
      console.error("Error loading products:", err);
      
      // Detect network errors
      const errorMsg = String(err?.message || err || "");
      const isNetworkError = 
        errorMsg.toLowerCase().includes("err_quic_protocol_error") ||
        errorMsg.toLowerCase().includes("failed to fetch") ||
        errorMsg.toLowerCase().includes("network") ||
        err?.name === "TypeError";
      
      if (isNetworkError) {
        setProductsError({
          message: "Network issue. Please retry.",
          isNetworkError: true,
        });
      } else {
        setProductsError({
          message: "Failed to load products",
          isNetworkError: false,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [studioId, studioIdValid, me]);

  const loadOffers = useCallback(async () => {
    // Resolve active studio ID
    const currentStudioId =
      studioId ||
      studioProfile?.studio_id ||
      profile?.studio_id ||
      null;

    if (!currentStudioId || !looksLikeUuid(currentStudioId)) return;

    // In-flight guard
    if (offersInFlightRef.current) return;
    offersInFlightRef.current = true;

    // Abort previous
    if (offersAbortRef.current) {
      try { offersAbortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    offersAbortRef.current = controller;

    try {
      const res = await apiFetch(`/api/studio/offers?studioId=${encodeURIComponent(currentStudioId)}`, {
        signal: controller.signal,
      });
      let json = null;
      try { json = await res.json(); } catch {}

      if (!res.ok) {
        const status = res.status;
        const code = json?.code;

        // Never retry on auth errors
        if (status === 401 || status === 403 || code === "AUTH_REQUIRED") {
          console.error("[Shop] loadOffers auth failed", { status, code, studioId: currentStudioId });
          offersAuthBlockedRef.current = true;
          if (!offersAuthNotifiedRef.current) {
            offersAuthNotifiedRef.current = true;
            notifyAndInbox?.({
              notification: {
                id: `shop-offers-auth-${currentStudioId || "studio"}`,
                title: "Shop",
                message: "Sign in required to load offers.",
                level: "warning",
                source: "shop",
                world: "studio",
                action: { kind: "NAVIGATE", to: "/auth" },
              },
            });
          }
          setOffersError({
            message: "Authentication failed. Please refresh.",
            code: code || String(status || "AUTH_REQUIRED"),
          });
          return;
        }

        // Retry only for 5xx, max 3
        const retryable = status >= 500 && status <= 599;
        if (retryable && offersAttemptRef.current < 3) {
          const attempt = offersAttemptRef.current++;
          const delay = Math.min(1000 * Math.pow(2, attempt), 4000); // 1s, 2s, 4s
          // release lock before scheduling; do NOT flip in timer
          offersInFlightRef.current = false;
          offersRetryTimerRef.current = setTimeout(() => {
            offersRetryTimerRef.current = null;
            loadOffers();
          }, delay);
          return;
        }

        console.error("[Shop] loadOffers failed", { status, json, studioId: currentStudioId });
        setOffersError({
          message: json?.error || `Offers failed (${status})`,
          code: code || String(status),
        });
        return;
      }

      // Success
      offersAttemptRef.current = 0;
      lastFetchedStudioUserIdRef.current = currentStudioId;
      const offersData = json?.offers || json?.data || [];
      if (import.meta.env?.DEV) {
        console.debug("[Shop] Offers loaded", {
          studioId: currentStudioId,
          offersCount: Array.isArray(offersData) ? offersData.length : 0,
        });
      }
      const byListing = {};
      offersData.forEach((offer) => {
        const listingKey = resolveOfferListingId(offer);
        if (!listingKey) return;
        (byListing[listingKey] ||= []).push(offer);
      });
      setOffersByListing(byListing);
      setOffersError(null);
      offersAuthBlockedRef.current = false;
      offersAuthNotifiedRef.current = false;

      // Sync listing state
      Object.entries(byListing).forEach(([listingKey, arr]) => {
        const latest = Array.isArray(arr) ? arr[0] : arr;
        if (!latest) return;
        const mappedStatus =
          OFFER_STATUS_TO_SHOP_STATUS[latest.status] || SHOP_STATUSES.LISTED;
        updateListingState(listingKey, (current) => ({
          ...current,
          status: mappedStatus,
          hasOffer: mappedStatus !== SHOP_STATUSES.LISTED,
          offerAmount: (latest.amount_cents || 0) / 100,
        }));
      });
    } catch (err) {
      if (err?.name === "AbortError") return;

      const authStatus = err?.status;
      const authCode = err?.data?.code || err?.data?.error?.code;
      if (authStatus === 401 || authStatus === 403 || authCode === "AUTH_REQUIRED") {
        console.warn("[Shop] loadOffers auth failed", {
          status: authStatus,
          code: authCode,
          studioId: currentStudioId,
        });
        offersAuthBlockedRef.current = true;
        if (!offersAuthNotifiedRef.current) {
          offersAuthNotifiedRef.current = true;
          notifyAndInbox?.({
            notification: {
              id: `shop-offers-auth-${currentStudioId || "studio"}`,
              title: "Shop",
              message: "Sign in required to load offers.",
              level: "warning",
              source: "shop",
              world: "studio",
              action: { kind: "NAVIGATE", to: "/auth" },
            },
          });
        }
        setOffersError({
          message: "Authentication failed. Please refresh.",
          code: authCode || String(authStatus || "AUTH_REQUIRED"),
        });
        return;
      }

      // Retry only for network errors, max 3
      if (offersAttemptRef.current < 3) {
        const attempt = offersAttemptRef.current++;
        const delay = Math.min(1000 * Math.pow(2, attempt), 4000);
        offersInFlightRef.current = false;
        offersRetryTimerRef.current = setTimeout(() => {
          offersRetryTimerRef.current = null;
          loadOffers();
        }, delay);
        return;
      }

      console.error("[Shop] loadOffers network failed (max retries)", { message: err?.message });
      setOffersError({ message: "Failed to fetch offers", code: "NETWORK_ERROR" });
    } finally {
      if (!offersRetryTimerRef.current) {
        offersInFlightRef.current = false;
      }
      setOffersLoaded(true);
    }
  }, [studioId, studioProfile, profile, apiFetch, updateListingState]);

  // Load offers once per user
  useEffect(() => {
    if (!user?.id) return;
    const key = `${user.id}:${activeStudioId || "none"}:${offersReloadTick}`;
    if (offersLastAttemptRef.current === key) return;
    if (offersAuthBlockedRef.current && offersReloadTick === 0) return;
    offersLastAttemptRef.current = key;
    loadOffers();
    return () => {
      if (offersRetryTimerRef.current) {
        clearTimeout(offersRetryTimerRef.current);
        offersRetryTimerRef.current = null;
      }
      if (offersAbortRef.current) {
        try { offersAbortRef.current.abort(); } catch {}
        offersAbortRef.current = null;
      }
      offersInFlightRef.current = false;
    };
  }, [user?.id, activeStudioId, loadOffers, offersReloadTick]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => {
      if (offersAuthBlockedRef.current) return;
      loadOffers();
    }, 30000);
    return () => clearInterval(interval);
  }, [user?.id, activeStudioId, loadOffers]);

  useEffect(() => {
    offersAuthBlockedRef.current = false;
    offersLastAttemptRef.current = "";
    offersAuthNotifiedRef.current = false;
  }, [user?.id, activeStudioId]);

  // Deep-link: capture IDs and open once data is ready
  useEffect(() => {
    if (!deepLinkThreadId || deepLinkThreadHandledRef.current) return;
    setPendingThreadId(deepLinkThreadId);
  }, [deepLinkThreadId]);

  useEffect(() => {
    if (!pendingThreadId || deepLinkThreadHandledRef.current) return;
    setInitialThreadId(pendingThreadId);
    setInboxOpen(true);
    deepLinkThreadHandledRef.current = true;
    setPendingThreadId(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("threadId");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }, [pendingThreadId]);

  useEffect(() => {
    if (!deepLinkOfferId || deepLinkOfferHandledRef.current) return;
    setPendingOfferId(deepLinkOfferId);
  }, [deepLinkOfferId]);

  // Deep-link: open offers modal for a specific offer (after offers load)
  useEffect(() => {
    if (!pendingOfferId || deepLinkOfferHandledRef.current || !offersLoaded) return;
    let matchedListing = null;
    let matchedOffers = [];
    Object.entries(offersByListing || {}).forEach(([listingId, offers]) => {
      if (matchedListing) return;
      const match = (offers || []).find((o) => String(o.id) === String(pendingOfferId));
      if (match) {
        matchedOffers = offers || [];
        matchedListing = listings.find((l) => String(l.id) === String(listingId)) || null;
      }
    });

    if (matchedOffers.length > 0 && matchedListing) {
      setSelectedListing(matchedListing);
      setSelectedOffers(matchedOffers);
      setOffersModalOpen(true);
      deepLinkOfferHandledRef.current = true;
    } else if (matchedOffers.length > 0 && !matchedListing) {
      notifyAndInbox?.({
        notification: {
          id: `shop-offer-missing-listing-${pendingOfferId}`,
          title: "Listing not found",
          message: "We found the offer but couldn't load its listing.",
          level: "warning",
          source: "shop",
          world: "studio",
          entity: { type: "offer", id: pendingOfferId },
          action: {
            kind: "NAVIGATE",
            to: "/dashboard/shop",
            params: { offerId: pendingOfferId },
          },
        },
      });
      deepLinkOfferHandledRef.current = true;
    } else if (offersLoaded) {
      notifyAndInbox?.({
        notification: {
          id: `shop-offer-missing-${pendingOfferId}`,
          title: "Offer not found",
          message: "We couldn't find that offer in your inbox.",
          level: "warning",
          source: "shop",
          world: "studio",
          entity: { type: "offer", id: pendingOfferId },
          action: {
            kind: "NAVIGATE",
            to: "/dashboard/shop",
            params: { offerId: pendingOfferId },
          },
        },
      });
      deepLinkOfferHandledRef.current = true;
    }
    if (deepLinkOfferHandledRef.current) {
      setPendingOfferId(null);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("offerId");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch {}
    }
  }, [offersByListing, offersLoaded, listings, notifyAndInbox, pendingOfferId]);

  // Push pending offers into NotificationContext so the topbar can show a badge
  useEffect(() => {
    if (!setOfferNotifications) return;

    const pending = [];
    const listingTitleById = new Map();
    listings.forEach((l) => {
      if (l?.id) listingTitleById.set(l.id, l.title || "Listing");
    });

    Object.entries(offersByListing || {}).forEach(([listingId, offers]) => {
      if (!Array.isArray(offers)) return;

      offers.forEach((offer) => {
        if (offer?.status !== "pending") return;

        const amount =
          typeof offer?.amount === "number"
            ? offer.amount
            : typeof offer?.amount_cents === "number"
            ? offer.amount_cents / 100
            : null;

        pending.push({
          id: offer?.id,
          listingId,
          amount,
          buyer: offer?.buyer_name || offer?.buyer_email || "Buyer",
          listingTitle: listingTitleById.get(listingId) || "Listing",
        });
      });
    });

    setOfferNotifications(pending);
  }, [offersByListing, listings, setOfferNotifications]);

  const handleOpenListingOffers = (listing) => {
    const offers = offersByListing[listing.id] || [];
    setSelectedListing(listing);
    setSelectedOffers(offers);
    setOffersModalOpen(true);
  };

  const handleOfferStatusChange = async (offer, newStatus) => {
    if (!offer?.id) return;
    setUpdatingOfferId(offer.id);
    try {
      const res = await apiFetch("/api/shop/offers-update-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: offer.id, status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        console.error("[Shop] offer status update error", data);
        return;
      }

      // Update local grouped state
      setOffersByListing((prev) => {
        const next = { ...prev };
        const listingKey = resolveOfferListingId(offer);
        if (!listingKey) return next;
        const arr = Array.isArray(next[listingKey]) ? [...next[listingKey]] : [];
        next[listingKey] = arr.map((o) =>
          o.id === offer.id ? { ...o, status: newStatus } : o
        );
        return next;
      });

      setSelectedOffers((prev) =>
        Array.isArray(prev)
          ? prev.map((o) => (o.id === offer.id ? { ...o, status: newStatus } : o))
          : prev
      );
    } catch (err) {
      console.error("[Shop] handleOfferStatusChange error", err);
    } finally {
      setUpdatingOfferId(null);
    }
  };

  const handleOfferModalSubmit = async (amount) => {
    if (!offerModalFor) return;
    const listing = offerModalFor;

    updateListingState(listing.id, (current) => ({
      ...current,
      status: SHOP_STATUSES.OFFER_PENDING,
      hasOffer: true,
      offerAmount: amount,
    }));

    try {
      const amountCents = Math.round(Number(amount || 0) * 100);
      if (!Number.isFinite(amountCents) || amountCents <= 0) {
        throw new Error("Invalid offer amount.");
      }

      const { data, error } = await offersApi.createOffer({
        listingId: listing.id,
        studioId: studioId || null,
        buyerId: userId || me,
        amountCents,
        note: null,
      });

      if (error) {
        console.error("[Shop] createOffer error", error);
        notifyAndInbox?.({
          notification: {
            id: `shop-offer-error-${listing.id}`,
            title: "Shop",
            message: error.message || "Failed to send offer.",
            level: "error",
            source: "shop",
            world: "studio",
            entity: { type: "listing", id: listing.id },
            action: { kind: "NAVIGATE", to: "/dashboard/shop" },
          },
        });
        return;
      }

      setOffersByListing((prev) => {
        const existing = Array.isArray(prev[listing.id]) ? prev[listing.id] : [];
        return {
          ...prev,
          [listing.id]: [data, ...existing],
        };
      });
    } catch (err) {
      console.error("[Shop] handleOfferModalSubmit error", err);
      notifyAndInbox?.({
        notification: {
          id: `shop-offer-error-${listing.id}-catch`,
          title: "Shop",
          message: err.message || "Failed to send offer.",
          level: "error",
          source: "shop",
          world: "studio",
          entity: { type: "listing", id: listing.id },
          action: { kind: "NAVIGATE", to: "/dashboard/shop" },
        },
      });
    } finally {
      setOfferModalFor(null);
    }
  };

  // persist filters
  useEffect(() => {
    writeLS(LS_FILTERS, {
      query,
      category,
      condition,
      minPrice,
      maxPrice,
      nearMe,
      radiusKm,
      locQuery,
      onlyMine,
      onlyFavs,
    });
  }, [query, category, condition, minPrice, maxPrice, nearMe, radiusKm, locQuery, onlyMine, onlyFavs]);

  // persist favorites and position only (products now in Supabase)
  useEffect(() => writeLS(LS_FAVS, Array.from(favs)), [favs]);
  useEffect(() => writeLS(LS_MY_POS, myPos), [myPos]);

  const categories = useMemo(
    () => [
      "all",
      ...Array.from(
        new Set(
          listings
            .map((p) => (p.category || "").trim())
            .filter(Boolean)
        )
      ),
    ],
    [listings]
  );
  const conditions = [
    "all",
    "New",
    "New - Open Box",
    "Used - Excellent",
    "Used - Good",
    "Used - Fair",
    "For parts",
  ];

  // top location autocomplete that sets myPos
  useEffect(() => {
    if (!locQuery || locQuery.length < 3) {
      setLocOpts([]);
      return;
    }
    clearTimeout(locTimer.current);
    const q = locQuery;
    lastQueryRef.current = q;
    locTimer.current = setTimeout(async () => {
      const results = await forwardGeocode(q, 6);
      if (lastQueryRef.current === q) setLocOpts(results);
    }, 280);
    return () => clearTimeout(locTimer.current);
  }, [locQuery]);

  async function setMyLocation() {
    if (!("geolocation" in navigator)) {
      alert("Geolocation not available.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords || {};
        const rev = await reverseGeocode(lat, lng);
        setMyPos({ lat, lng, label: rev.label });
      },
      (err) => {
        console.warn(err);
        alert("Couldn't get your location. Check permissions.");
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 600000,
      }
    );
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const lq = locQuery.trim().toLowerCase();
    const min = minPrice !== "" ? Number(minPrice) : -Infinity;
    const max = maxPrice !== "" ? Number(maxPrice) : Infinity;
    let arr = listings
      .filter((l) => {
        if (l.status !== "Sold") return true;
        const soldState = getSoldState(l);
        return soldState.isInGrace;
      })
      .filter((l) => !onlyMine || l.createdBy === me)
      .filter((l) => !onlyFavs || favs.has(l.id))
      .filter(
        (l) =>
          category === "all" ||
          (l.category || "").toLowerCase() === category.toLowerCase()
      )
      .filter(
        (l) =>
          condition === "all" ||
          (l.condition || "").toLowerCase() === condition.toLowerCase()
      )
      .filter((l) => (l.price ?? 0) >= min && (l.price ?? 0) <= max)
      .filter((l) => {
        if (!q) return true;
        const txt = `${l.title} ${l.description} ${l.location || ""}`.toLowerCase();
        return txt.includes(q);
      })
      .filter((l) => {
        if (!lq) return true;
        return (l.location || "").toLowerCase().includes(lq);
      });
    if (nearMe && myPos?.lat != null && myPos?.lng != null) {
      arr = arr.filter(
        (l) =>
          kmBetween(
            { lat: myPos.lat, lng: myPos.lng },
            { lat: l.lat, lng: l.lng }
          ) <= Math.max(1, Number(radiusKm || 1))
      );
    }
    return arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [
    listings,
    query,
    category,
    condition,
    minPrice,
    maxPrice,
    onlyMine,
    onlyFavs,
    favs,
    me,
    nearMe,
    radiusKm,
    myPos,
    locQuery,
  ]);

  const selectOfferForProgress = (offers) => {
    if (!Array.isArray(offers) || offers.length === 0) return null;
    const paidHeld = offers.find(
      (offer) => offer?.status === "accepted" && offer?.order_status === "paid_held"
    );
    if (paidHeld) return paidHeld;

    const prioritizedStatuses = [
      "disputed",
      "completed",
      "handed_over",
      "accepted",
      "pending",
    ];

    for (const status of prioritizedStatuses) {
      const match = offers.find((offer) => offer?.status === status);
      if (match) {
        return match;
      }
    }

    return offers[0] || null;
  };

  async function upsertListing(data) {
    console.log("?? upsertListing called with:", data);
    console.log("?? Current studioId:", studioId, "valid:", studioIdValid);

    if (!studioId || !studioIdValid) {
      const msg = !studioId
        ? "Please set up your studio ID first."
        : "Your Studio ID is not a valid UUID. Please set a valid studios.id from Supabase.";
      console.error("? Studio ID validation failed:", msg);
      fireToast(msg, "warn");
      if (notifyAndInbox) {
        notifyAndInbox({
          notification: {
            id: `shop-studio-invalid-${studioId || "missing"}`,
            title: "Shop",
            message: msg,
            level: "warning",
            source: "shop",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/shop" },
          },
        });
      } else {
        alert(msg);
      }
      return;
    }

    // If the user selected new images, upload them (thumb+display) first, then
    // save the display URLs into `image_gallery` / `main_image_url`.
    let finalImages = Array.isArray(data?.images) ? data.images.filter(Boolean) : [];
    let imageFiles = Array.isArray(data?.imageFiles) ? data.imageFiles : [];
    if (imageFiles.length < finalImages.length) {
      imageFiles = imageFiles.concat(
        new Array(finalImages.length - imageFiles.length).fill(null)
      );
    }
    if (imageFiles.length > finalImages.length) {
      imageFiles = imageFiles.slice(0, finalImages.length);
    }

    const uploadIndices = [];
    const uploadFiles = [];
    for (let i = 0; i < finalImages.length; i += 1) {
      const f = imageFiles[i];
      if (f instanceof File || f instanceof Blob) {
        uploadIndices.push(i);
        uploadFiles.push(f);
      }
    }

    if (uploadFiles.length > 0) {
      try {
        fireToast("Uploading images…", "info", 1800);
        const uploaded = await uploadProductImages({
          studioId,
          files: uploadFiles,
          variants: true,
        });
        const displayUrls =
          uploaded?.displayUrls || uploaded?.urls || [];
        for (let j = 0; j < uploadIndices.length; j += 1) {
          const targetIndex = uploadIndices[j];
          const url = displayUrls[j];
          if (url) finalImages[targetIndex] = url;
        }
      } catch (e) {
        console.error("Error uploading product images:", e);
        fireToast(
          `Failed to upload images: ${e?.message || "Unknown error"}`,
          "error"
        );
        return;
      }
    }

    // Never persist data URLs to the DB.
    finalImages = finalImages.filter(
      (u) => !(typeof u === "string" && u.startsWith("data:"))
    );

    // Convert to Supabase format
    const supabaseData = {
      title: data.title || "Untitled",
      description: data.description || null,
      price_cents: Math.round((data.price || 0) * 100), // Convert dollars to cents
      currency: "AUD",
      image_gallery: finalImages.length > 0 ? finalImages : null,
      main_image_url: finalImages[0] ? finalImages[0] : null,
      is_published: false, // Start as draft
      is_active: true,
    };

    if (edit) {
      // Update existing
      const { error } = await productsApi.updateProduct(edit.id, supabaseData);

      if (error) {
        console.error("Error updating product:", error);
        fireToast("Failed to save listing", "error");
        notifyAndInbox?.({
          notification: {
            id: `shop-update-product-${edit.id}-error`,
            title: "Shop",
            message: `Failed to update product: ${error.message || "Unknown error"}`,
            level: "error",
            source: "shop",
            world: "studio",
            entity: { type: "listing", id: edit.id },
            action: { kind: "NAVIGATE", to: "/dashboard/shop" },
          },
        });
        return;
      }

      // Refresh from Supabase to ensure local state reflects server
      const { data: refreshed, error: refreshErr } =
        await productsApi.listProductsForStudio(studioId);
      if (refreshErr) {
        console.error("Error refreshing products after update:", refreshErr);
      } else {
        const converted = (refreshed || []).map((p) => ({
          id: p.id,
          title: p.title,
          description: p.description || "",
          price: Math.round((p.price_cents || 0) / 100),
          category: p.category || "",
          condition: p.condition || "",
          location: p.location || "",
          lat: p.lat || null,
          lng: p.lng || null,
          images: p.image_gallery || [],
          status: p.is_active ? "Available" : "Sold",
          createdBy: me,
          createdAt: new Date(p.created_at).getTime(),
          isPublished: p.is_published,
          sold_at: p.sold_at || null,
        }));
        setListings(converted);
      }

      fireToast("Listing saved", "success");
      setEdit(null);
      setModalOpen(false);
    } else {
      // Create new
      console.log("?? Creating new product for studio:", studioId);
      console.log("?? Product data:", supabaseData);

      const { data: created, error } =
        await productsApi.createProductForStudio(studioId, supabaseData);

      console.log("?? createProductForStudio result:", { created, error });

      if (error) {
        console.error("? Error creating product:", error);
        console.error("? Error details:", JSON.stringify(error, null, 2));
        console.error("? Attempted to save:", supabaseData);
        fireToast("Failed to save listing", "error");
        notifyAndInbox?.({
          notification: {
            id: `shop-create-product-error-${studioId || "studio"}`,
            title: "Shop",
            message: `Failed to create product: ${error.message || JSON.stringify(error)}`,
            level: "error",
            source: "shop",
            world: "studio",
            action: { kind: "NAVIGATE", to: "/dashboard/shop" },
          },
        });
        return;
      }

      console.log("? Product created successfully:", created);

      // Refresh from Supabase to ensure local state reflects server
      const { data: refreshed, error: refreshErr } =
        await productsApi.listProductsForStudio(studioId);
      if (refreshErr) {
        console.error("Error refreshing products after create:", refreshErr);
      } else {
        console.log("? Refreshed product list, count:", refreshed?.length || 0);
        const converted = (refreshed || []).map((p) => ({
          id: p.id,
          title: p.title,
          description: p.description || "",
          price: Math.round((p.price_cents || 0) / 100),
          category: p.category || "",
          condition: p.condition || "",
          location: p.location || "",
          lat: p.lat || null,
          lng: p.lng || null,
          images: p.image_gallery || [],
          status: p.is_active ? "Available" : "Sold",
          createdBy: me,
          createdAt: new Date(p.created_at).getTime(),
          isPublished: p.is_published,
          sold_at: p.sold_at || null,
        }));
        setListings(converted);
      }
      setModalOpen(false);
      fireToast("Listing saved", "success");
      notifyAndInbox?.({
        notification: {
          id: `shop-create-product-${created?.id || supabaseData.title || "new"}`,
          title: "Shop",
          message: `Product "${supabaseData.title}" created successfully!`,
          level: "success",
          source: "shop",
          world: "studio",
          entity: created?.id ? { type: "listing", id: created.id } : null,
          action: { kind: "NAVIGATE", to: "/dashboard/shop" },
        },
      });
    }
  }

  const toggleFav = (id) =>
    setFavs((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  async function markSold(id) {
    const soldAtIso = new Date().toISOString();
    const { error } = await productsApi.updateProduct(id, {
      is_active: false,
      sold_at: soldAtIso,
    });
    if (error) {
      console.error("Error marking sold:", error);
      notifyAndInbox?.({
        notification: {
          id: `shop-mark-sold-${id}-error`,
          title: "Shop",
          message: `Failed to mark as sold: ${error.message || "Unknown error"}`,
          level: "error",
          source: "shop",
          world: "studio",
          entity: { type: "listing", id },
          action: { kind: "NAVIGATE", to: "/dashboard/shop" },
        },
      });
      return;
    }
    setListings((prev) =>
      prev.map((l) =>
        l.id === id ? { ...l, status: "Sold", sold_at: soldAtIso } : l
      )
    );
  }

  async function removeListing(id) {
    if (!confirm("Delete this listing?")) return;

    const { error } = await productsApi.deleteProduct(id);
    if (error) {
      console.error("Error deleting product:", error);
      notifyAndInbox?.({
        notification: {
          id: `shop-delete-product-${id}-error`,
          title: "Shop",
          message: `Failed to delete product: ${error.message || "Unknown error"}`,
          level: "error",
          source: "shop",
          world: "studio",
          entity: { type: "listing", id },
          action: { kind: "NAVIGATE", to: "/dashboard/shop" },
        },
      });
      return;
    }

    setListings((prev) => prev.filter((l) => l.id !== id));
  }

  async function togglePublish(id, currentPublishState) {
    // Block publishing paid listings without Stripe Connect
    try {
      const listing = listings.find((l) => l.id === id);
      const isPaid = Number(listing?.price || 0) > 0;
      if (!currentPublishState && isPaid && !isStripeConnected) {
        const res = await ensureStripeConnected({ studioId: activeStudioId });
        if (!res?.ok) {
          notifyAndInbox?.({
            notification: {
              id: `shop-stripe-required-${id}`,
              title: "Stripe",
              message: res?.error || "Connect Stripe to publish paid listings.",
              level: "warning",
              source: "shop",
              world: "studio",
              entity: { type: "listing", id },
              action: { kind: "NAVIGATE", to: "/dashboard/shop" },
            },
          });
        }
        return; // do not proceed with publish
      }
    } catch {}

    const nextPublishState = !currentPublishState;
    const publishPayload = nextPublishState
      ? { is_published: true, is_active: true }
      : { is_published: false };

    const { error } = await productsApi.updateProduct(id, publishPayload);

      if (error) {
        const isValidationError =
          ["22023", "PGRST22023"].includes(error.code) ||
          (error?.message || "").includes("Cannot publish");
        const diagMessage = isValidationError
          ? "Add a price, currency, and studio before publishing this product."
          : `Failed to update publish status: ${error.message || "Unknown error"}`;

        if (isValidationError) {
          console.warn("[Shop] publish validation failed:", error);
        } else {
          console.error("Error toggling publish:", error);
        }

        notifyAndInbox?.({
          notification: {
            id: `shop-publish-${id}-${isValidationError ? "validation" : "error"}`,
            title: isValidationError ? "Listing details" : "Shop",
            message: diagMessage,
            level: isValidationError ? "warning" : "error",
            source: "shop",
            world: "studio",
            entity: { type: "listing", id },
            action: { kind: "NAVIGATE", to: "/dashboard/shop" },
          },
        });
        return;
      }

    setListings((prev) =>
      prev.map((l) =>
        l.id === id ? { ...l, isPublished: nextPublishState, is_active: publishPayload.is_active ?? l.is_active } : l
      )
    );
  }

  function reportListing(l) {
    alert("Thanks for reporting. We’ll review this listing.");
    // TODO(server): send report with listing id + metadata
  }
  return (
    <PageLayout
      header={<PageHeader title="Shop" subtitle="Marketplace" showBack />}
    >
      <style>{`
        .mudo-dark-select, .mudo-dark-select option { background:#1e1f22 !important; color:#fff !important; }
        .image-hover-zoom:hover .hover-scale-image { transform: scale(1.05); }
      `}</style>

      <WidgetShell className="mb-3">
        <div className="text-sm text-[var(--mudo-text)]">
          <strong>Note:</strong>{" "}
          Payments in Mudo Shop are processed via Stripe. Buyer pays, Mudo takes a small 5%
          platform fee, and ~95% is paid out to the studio&apos;s connected Stripe account
          after completion.{" "}
          <Button
            variant="ghost"
            className="px-0 py-0 text-xs underline"
            onClick={() => navigate("/dashboard/legal/terms")}
          >
            View Terms &amp; Conditions
          </Button>
        </div>
      </WidgetShell>

      {/* Studio UUID Status (show user what they have) */}
      {studioId && studioIdValid && (
        <WidgetShell className="mb-3">
          <strong>✓ Studio Connected</strong>{" "}
          <span className="text-[11px] text-[var(--mudo-text-muted)] ml-2">
            ID:{" "}
            <code className="px-2 py-1 rounded bg-[var(--mudo-bg-soft)] border border-[var(--mudo-border)]">
              {studioId.slice(0, 8)}...
            </code>
          </span>
        </WidgetShell>
      )}

      {/* Studio ID Warning - Simplified */}
      {(!studioId || !studioIdValid) && (
        <WidgetShell className="mb-3">
          <strong>⚠ Studio Setup Required</strong>
          <p className="mt-2 text-sm text-[var(--mudo-text-muted)]">
            To create listings, you need to set up your studio in{" "}
            <strong>Customize Studio</strong> (top menu).
            <br />
            <span className="text-[11px] text-[var(--mudo-text-muted)]">
              One-time setup connects your studio to the marketplace, workshops, and
              bookable spaces.
            </span>
          </p>
        </WidgetShell>
      )}

      {/* Filters and header */}
      <WidgetShell className="w-full rounded-2xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] mb-4">
        <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
          {/* Title + inbox */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-[var(--mudo-text-muted)]">
                Buy &amp; sell studio gear - kilns, wheels, tools, materials. Local-first marketplace.
              </p>
              {offersErrorMessage && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2">
                  <span className="text-[12px] text-red-400 font-medium">
                    {offersErrorMessage}
                    {offersErrorCode ? ` (${offersErrorCode})` : ""}
                  </span>
                  {offersAuthError && (
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() => navigate("/auth")}
                    >
                      Sign in
                    </Button>
                  )}
                  {!offersAuthError && (
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() => {
                        setOffersError(null);
                        offersFailCountRef.current = 0;
                        offersAuthBlockedRef.current = false;
                        setOffersReloadTick((t) => t + 1);
                      }}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Inbox Button (logic unchanged) */}
              <Button
                variant="soft"
                className="relative flex items-center gap-2 px-3 py-2"
                onClick={() => setInboxOpen(true)}
                title="Messages"
              >
                <Mail size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center px-[6px]">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Search + filters card */}
          <WidgetShell title="Filters" className="bg-[var(--mudo-bg-soft)] border-[var(--mudo-border)]">
            {/* Search row */}
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="flex-1">
                <input
                  className="w-full rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2 text-sm text-[var(--mudo-text)] placeholder:text-[var(--mudo-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]"
                  placeholder="Search listings…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Filters rows */}
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="flex flex-wrap gap-2 md:items-center">
                {/* Category */}
                <select
                  className="rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2 text-sm text-[var(--mudo-text)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)] min-w-[180px]"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c === "all" ? "All categories" : c}
                    </option>
                  ))}
                </select>

                {/* Condition */}
                <select
                  className="rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2 text-sm text-[var(--mudo-text)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)] min-w-[180px]"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  {conditions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                {/* Location text + autocomplete that sets myPos when chosen */}
                <div className="relative">
                  <input
                    className="rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2 text-sm text-[var(--mudo-text)] placeholder:text-[var(--mudo-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]"
                    placeholder="Location text…"
                    value={locQuery}
                    onChange={(e) => setLocQuery(e.target.value)}
                    title="Filter by location name; click a suggestion to set your location"
                  />
                  {locOpts.length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] max-h-56 overflow-y-auto z-20 shadow-lg">
                      {locOpts.map((o, i) => (
                        <div
                          key={`${o.lat},${o.lng},${i}`}
                          role="button"
                          onMouseDown={() => {
                            setMyPos({
                              lat: o.lat,
                              lng: o.lng,
                              label: o.label,
                            });
                            setLocOpts([]);
                          }}
                          className="px-3 py-2 text-sm cursor-pointer hover:bg-white/5"
                        >
                          {o.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Near me + radius */}
                <Button
                  variant={nearMe ? "primary" : "soft"}
                  className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] hover:border-[var(--mudo-primary)]"
                  onClick={() => setNearMe((v) => !v)}
                >
                  Near me
                </Button>
                <input
                  className="rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-3 py-2 text-sm text-[var(--mudo-text)] placeholder:text-[var(--mudo-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)] disabled:opacity-50 w-28"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Radius (km)"
                  value={radiusKm}
                  onChange={(e) =>
                    setRadiusKm(Math.max(1, Number(e.target.value || 1)))
                  }
                  disabled={!nearMe}
                />

                <Button
                  variant="soft"
                  className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] hover:border-[var(--mudo-primary)]"
                  onClick={setMyLocation}
                  title="Use geolocation"
                >
                  {myPos?.label ? `My location: ${myPos.label}` : "Set my location"}
                </Button>
                {myPos && (
                  <Button
                    variant="soft"
                    className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] hover:border-[var(--mudo-primary)]"
                    onClick={() => setMyPos(null)}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {/* Right-aligned actions */}
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Button
                  variant={onlyMine ? "primary" : "soft"}
                  className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] hover:border-[var(--mudo-primary)]"
                  onClick={() => setOnlyMine((v) => !v)}
                >
                  My listings
                </Button>
                <Button
                  variant={onlyFavs ? "primary" : "soft"}
                  className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] text-[var(--mudo-text)] hover:border-[var(--mudo-primary)]"
                  onClick={() => setOnlyFavs((v) => !v)}
                >
                  {onlyFavs ? "Saved" : "Favourites"}
                </Button>

                <Button
                  variant="primary"
                  className="text-sm px-3 py-2 rounded-xl border border-[var(--mudo-border)] bg-amber-500/25 text-amber-100 hover:bg-amber-500/35"
                  onClick={() => {
                    setEdit(null);
                    setModalOpen(true);
                  }}
                >
                  + New listing
                </Button>
              </div>
            </div>
          </WidgetShell>
        </div>
      </WidgetShell>
      {/* ========== END NEW HEADER ========== */}

      {loading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-6 py-8 text-sm text-[var(--mudo-text-muted)]">
          <div className="inline-block w-8 h-8 rounded-full border-2 border-[var(--mudo-border)] border-t-white animate-spin" />
          <p className="mt-3 opacity-80">Loading products...</p>
        </div>
      ) : productsError ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-red-500/30 bg-[var(--mudo-bg-soft)] px-6 py-8 text-sm">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="text-2xl">⚠️</div>
            <p className="font-semibold text-red-400">
              {productsError.message}
            </p>
            {productsError.isNetworkError && (
              <p className="text-xs text-[var(--mudo-text-muted)] max-w-md">
                This could be a temporary network issue or a protocol error (ERR_QUIC_PROTOCOL_ERROR).
                Please try again.
              </p>
            )}
          </div>
          <Button
            variant="primary"
            onClick={loadProducts}
            className="px-6 py-2 text-sm"
          >
            Retry
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-6 py-6 text-sm text-[var(--mudo-text-muted)]">
          No listings match your filters. Try clearing search or add a{" "}
          <span className="font-semibold">&nbsp;New listing</span>.
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
          {filtered.map((l) => {
            const mine = l.createdBy === me;
            const isFav = favs.has(l.id);
            const images = Array.isArray(l.images) ? l.images.filter(Boolean) : [];
            const cover = images[0];
            const uiState = getListingState(l.id);
            const status = uiState.status;
            const label = SHOP_STATUS_LABELS[status] || "Open";
            const isSeller = l.sellerId && userId ? l.sellerId === userId : mine;
            const listingOffers = offersByListing[l.id] || [];
            const visibleOffers = listingOffers.filter(
              (o) => o && !["declined", "deleted", "cancelled"].includes(o.status)
            );
            const activeOffer = selectOfferForProgress(visibleOffers);
            if (import.meta.env?.DEV && activeOffer) {
              console.debug("[Shop] selected offer for progress", {
                listingId: l.id,
                selectedOfferId: activeOffer?.id,
                status: activeOffer?.status,
                orderStatus: activeOffer?.order_status,
              });
            }
            const activeOfferStatus = activeOffer?.status || null;
            const activeOrderStatus = activeOffer?.order_status || null;
            const isPaidHeld = activeOrderStatus === "paid_held";
            const isAccepted = activeOfferStatus === "accepted";
            const soldState = getSoldState(l);
            const isSoldVisible = l.status === "Sold" && soldState.isInGrace;
            const soldRemainingHours = isSoldVisible
              ? Math.max(1, soldState.graceHoursLeft || 0)
              : 0;
            if (import.meta.env?.DEV && activeOffer && isAccepted) {
              console.debug("[Shop] CTA logic", {
                offerId: activeOffer?.id,
                offerStatus: activeOffer?.status,
                orderStatus: activeOffer?.order_status,
                isPaidHeld,
                isAccepted,
              });
            }
            const isMarketplaceOffer = !!activeOffer?.listing_id;
            const progressSteps = [
              "Offer sent",
              "Offer accepted",
              "Payment held",
              "Seller fulfils",
              "Buyer confirms",
              "Payout released",
            ];
            const currentStepIndex = (() => {
              if (!activeOffer) return -1;
              if (activeOfferStatus === "disputed") return isPaidHeld ? 2 : 1;
              if (activeOfferStatus === "completed") return 5;
              if (activeOfferStatus === "handed_over") return isPaidHeld ? 3 : 2;
              if (activeOfferStatus === "accepted") return isPaidHeld ? 2 : 1;
              if (activeOfferStatus === "pending") return 0;
              return 0;
            })();
            const nextLabel = (() => {
              if (!activeOffer) return "";
              if (activeOfferStatus === "disputed") return "Issue reported";
              if (activeOfferStatus === "completed") return "Complete";
              if (activeOfferStatus === "handed_over" && isPaidHeld) {
                return "Next: Confirm received";
              }
              if (activeOfferStatus === "accepted" && !isPaidHeld) {
                return "Next: Pay now";
              }
              if (isPaidHeld && activeOfferStatus !== "handed_over") {
                return "Next: Seller fulfils / ships";
              }
              if (activeOfferStatus === "pending") return "Next: Seller accepts";
              return "Next: Pay now";
            })();

            const handleMakeOffer = () => setOfferModalFor(l);
            const handleSubmitOffer = async (amount) => {
              updateListingState(l.id, (current) => ({
                ...current,
                status: SHOP_STATUSES.OFFER_PENDING,
                hasOffer: true,
                offerAmount: amount,
              }));
              try {
                const amountCents = Math.round(Number(amount || 0) * 100);
                if (!Number.isFinite(amountCents) || amountCents <= 0) {
                  throw new Error("Invalid offer amount.");
                }

                const { data, error } = await offersApi.createOffer({
                  listingId: l.id,
                  studioId: studioId || null,
                  buyerId: userId || me,
                  amountCents,
                  note: null,
                });

                if (error) {
                  console.error("[Shop] createOffer error", error);
                  notifyAndInbox?.({
                    notification: {
                      id: `shop-offer-create-${l.id}-error`,
                      title: "Shop",
                      message: error.message || "Failed to send offer.",
                      level: "error",
                      source: "shop",
                      world: "studio",
                      entity: { type: "listing", id: l.id },
                      action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                    },
                  });
                  return;
                }

                setOffersByListing((prev) => {
                  const existing = Array.isArray(prev[l.id]) ? prev[l.id] : [];
                  return {
                    ...prev,
                    [l.id]: [data, ...existing],
                  };
                });
              } catch (err) {
                console.error("[Shop] handleSubmitOffer error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-offer-submit-${l.id}-error`,
                    title: "Shop",
                    message: err.message || "Failed to send offer.",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              } finally {
                setOfferModalFor(null);
              }
            };
            const handleAcceptOffer = async () => {
              if (!activeOffer) return;
              try {
                const { data, error } = await offersApi.updateOfferStatus(
                  activeOffer.id,
                  "accepted"
                );
                if (error) {
                  console.error("[Shop] updateOfferStatus(accepted) error", error);
                  notifyAndInbox?.({
                    notification: {
                      id: `shop-offer-accept-${activeOffer.id}-error`,
                      title: "Shop",
                      message: error.message || "Failed to accept offer.",
                      level: "error",
                      source: "shop",
                      world: "studio",
                      entity: { type: "offer", id: activeOffer.id },
                      action: {
                        kind: "NAVIGATE",
                        to: "/dashboard/shop",
                        params: { offerId: activeOffer.id },
                      },
                    },
                  });
                  return;
                }
                const mappedStatus =
                  OFFER_STATUS_TO_SHOP_STATUS[data?.status] || SHOP_STATUSES.LISTED;
                updateListingState(l.id, (current) => ({
                  ...current,
                  status: mappedStatus,
                  hasOffer: mappedStatus !== SHOP_STATUSES.LISTED,
                }));
                setOffersByListing((prev) => {
                  const arr = Array.isArray(prev[l.id]) ? prev[l.id] : [];
                  return {
                    ...prev,
                    [l.id]: arr.map((o) => (o.id === activeOffer.id ? data : o)),
                  };
                });
              } catch (err) {
                console.error("[Shop] handleAcceptOffer error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-offer-accept-${activeOffer?.id || l.id}-error-catch`,
                    title: "Shop",
                    message: err.message || "Failed to accept offer.",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: activeOffer?.id ? { type: "offer", id: activeOffer.id } : { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              }
            };
            const handleRejectOffer = async () => {
              if (!activeOffer) return;
              try {
                const { data, error } = await offersApi.updateOfferStatus(
                  activeOffer.id,
                  "declined"
                );
                if (error) {
                  console.error("[Shop] updateOfferStatus(declined) error", error);
                  notifyAndInbox?.({
                    notification: {
                      id: `shop-offer-decline-${activeOffer.id}-error`,
                      title: "Shop",
                      message: error.message || "Failed to reject offer.",
                      level: "error",
                      source: "shop",
                      world: "studio",
                      entity: { type: "offer", id: activeOffer.id },
                      action: {
                        kind: "NAVIGATE",
                        to: "/dashboard/shop",
                        params: { offerId: activeOffer.id },
                      },
                    },
                  });
                  return;
                }
                const mappedStatus =
                  OFFER_STATUS_TO_SHOP_STATUS[data?.status] || SHOP_STATUSES.LISTED;
                updateListingState(l.id, (current) => ({
                  ...current,
                  status: mappedStatus,
                  hasOffer: mappedStatus !== SHOP_STATUSES.LISTED,
                  offerAmount: mappedStatus === SHOP_STATUSES.LISTED ? null : current.offerAmount,
                }));
                setOffersByListing((prev) => {
                  const arr = Array.isArray(prev[l.id]) ? prev[l.id] : [];
                  return {
                    ...prev,
                    [l.id]: arr.map((o) => (o.id === activeOffer.id ? data : o)),
                  };
                });
              } catch (err) {
                console.error("[Shop] handleRejectOffer error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-offer-decline-${activeOffer?.id || l.id}-error-catch`,
                    title: "Shop",
                    message: err.message || "Failed to reject offer.",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: activeOffer?.id ? { type: "offer", id: activeOffer.id } : { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              }
            };

            // Marketplace Payment via PaymentIntent (Stripe.js)
            const handlePayNow = async () => {
              try {
                setPaymentLoadingId(l.id);

                const priceNumber = Number(l.price || 0);
                if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
                  throw new Error("Invalid price for this listing.");
                }

                const res = await apiFetch("/api/shop/order-checkout", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    orderId: activeOffer?.order_id || null,
                    offerId: activeOffer?.id || null,
                  }),
                });

                if (!res.ok) {
                  const errJson = await res.json().catch(() => ({}));
                  throw new Error(errJson?.error || `Checkout failed (${res.status})`);
                }

                const { clientSecret } = await res.json();
                if (!clientSecret) {
                  throw new Error("Missing clientSecret from checkout API.");
                }

                // Open Stripe Elements modal so the buyer can attach a payment method and confirm.
                setPayModal({ open: true, clientSecret, returnTo: "/dashboard/shop" });
              } catch (err) {
                console.error("[Shop] Pay now error", err);
                const msg = err?.message || "Failed to process payment.";
                if (notifyAndInbox) {
                  notifyAndInbox({
                    notification: {
                      id: `shop-pay-${l.id}-error`,
                      title: "Shop",
                      message: msg,
                      level: "error",
                      source: "shop",
                      world: "studio",
                      entity: { type: "listing", id: l.id },
                      action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                    },
                  });
                } else {
                  alert(msg);
                }
              } finally {
                setPaymentLoadingId(null);
              }
            };

            const handleMarkHandedOver = async () => {
              if (!activeOffer) return;
              try {
                const res = await apiFetch("/api/shop/offers-status", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    offerId: activeOffer.id,
                    status: "handed_over",
                  }),
                });
                if (!res.ok) {
                  throw new Error("Failed to mark handed over");
                }
                updateListingState(l.id, (current) => ({
                  ...current,
                  status: SHOP_STATUSES.AWAITING_BUYER_CONFIRMATION,
                }));
              } catch (err) {
                console.error("[Shop] markHandedOver error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-handed-over-${activeOffer?.id || l.id}-error`,
                    title: "Shop",
                    message: err?.message || "Could not mark as handed over",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: activeOffer?.id ? { type: "offer", id: activeOffer.id } : { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              }
            };
            const handleBuyerConfirmReceived = async () => {
              if (!activeOffer) return;
              try {
                const res = await apiFetch("/api/shop/offers-status", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    offerId: activeOffer.id,
                    status: "completed",
                  }),
                });
                if (!res.ok) {
                  throw new Error("Failed to confirm receipt");
                }
                updateListingState(l.id, (current) => ({
                  ...current,
                  status: SHOP_STATUSES.COMPLETED,
                }));
              } catch (err) {
                console.error("[Shop] buyerConfirmReceived error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-buyer-confirm-${activeOffer?.id || l.id}-error`,
                    title: "Shop",
                    message: err?.message || "Could not confirm receipt",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: activeOffer?.id ? { type: "offer", id: activeOffer.id } : { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              }
            };
            const handleBuyerReportIssue = async () => {
              if (!activeOffer) return;
              try {
                const res = await apiFetch("/api/shop/offers-status", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    offerId: activeOffer.id,
                    status: "disputed",
                  }),
                });
                if (!res.ok) {
                  throw new Error("Failed to report issue");
                }
                updateListingState(l.id, (current) => ({
                  ...current,
                  status: SHOP_STATUSES.DISPUTED,
                }));
              } catch (err) {
                console.error("[Shop] buyerReportIssue error", err);
                notifyAndInbox?.({
                  notification: {
                    id: `shop-buyer-dispute-${activeOffer?.id || l.id}-error`,
                    title: "Shop",
                    message: err?.message || "Could not report issue",
                    level: "error",
                    source: "shop",
                    world: "studio",
                    entity: activeOffer?.id ? { type: "offer", id: activeOffer.id } : { type: "listing", id: l.id },
                    action: { kind: "NAVIGATE", to: "/dashboard/shop" },
                  },
                });
              }
            };

            const isPaying = paymentLoadingId === l.id;

            return (
              <div
                key={l.id}
                className="flex flex-col gap-2 rounded-2xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] p-3"
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "3px 10px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.16)",
                    background: "rgba(255,255,255,0.06)",
                    fontSize: 11,
                    marginBottom: 6,
                  }}
                >
                  {label}
                  {isPaidHeld && (
                    <span
                      style={{
                        marginLeft: 6,
                        opacity: 0.85,
                        color: "#93c5fd",
                      }}
                    >
                      • Paid (Held)
                    </span>
                  )}
                  {uiState.offerAmount ? (
                    <span
                      style={{
                        marginLeft: 6,
                        opacity: 0.8,
                      }}
                    >
                      • ${Number(uiState.offerAmount).toLocaleString()}
                    </span>
                  ) : null}
                </div>

                {cover && (
                  <div
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.08)",
                      cursor: "pointer",
                    }}
                    className="image-hover-zoom"
                    onClick={() => {
                      if (images.length > 0) {
                        window.open(images[0], "_blank");
                      }
                    }}
                    title={
                      l.images?.length > 1
                        ? `Click to view all ${l.images.length} images`
                        : "Click to view full size"
                    }
                  >
                    <ImageCarousel
                      images={images}
                      alt="Listing image"
                      aspect="4/3"
                      className="rounded-none"
                      showDots={false}
                      showCounter={false}
                      imgClassName="hover-scale-image"
                    />
                    {isSoldVisible && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.35))",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                          gap: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 22,
                            letterSpacing: "0.45em",
                            color: "rgba(255,255,255,0.95)",
                            fontWeight: 700,
                            textShadow: "0 0 16px rgba(0,0,0,0.6)",
                            transform: "rotate(-5deg)",
                          }}
                        >
                          SOLD
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            letterSpacing: "0.3em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.8)",
                          }}
                        >
                          Auto-unpublishes in {soldRemainingHours}h
                        </span>
                      </div>
                    )}
                    {l.images?.length > 1 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          background: "rgba(0,0,0,0.7)",
                          padding: "4px 8px",
                          borderRadius: 6,
                          fontSize: 11,
                          color: "white",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        📷 {l.images.length}
                      </div>
                    )}
                  </div>
                )}

                <h3 style={title}>
                  {l.title || "Untitled"}
                  {mine && !l.isPublished && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: "rgba(251,191,36,0.25)",
                        border: "1px solid rgba(251,191,36,0.35)",
                      }}
                    >
                      DRAFT
                    </span>
                  )}
                </h3>
                {isSoldVisible && soldRemainingHours > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "#fbbf24",
                      marginTop: -4,
                      marginBottom: 8,
                    }}
                  >
                    Sold · auto-unpublishes in {soldRemainingHours}h
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 8,
                  }}
                >
                  <div style={price}>
                    {typeof l.price === "number"
                      ? `$${l.price.toLocaleString()}`
                      : "—"}
                  </div>
                  <div
                    style={{
                      ...meta,
                      opacity: 0.9,
                    }}
                  >
                    {l.location || "—"}
                  </div>
                </div>
                <p style={meta}>
                  {l.category || "Uncategorised"} • {l.condition || "—"} • {l.status}
                </p>
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.7,
                  }}
                >
                  Flow status (visual only): offer ? accept ? pay ? handover ? buyer
                  confirms ? payout (95% seller / 5% Mudo).
                </div>
                {activeOffer && (
                  <div
                    style={{
                      marginTop: 8,
                      borderRadius: 10,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      padding: "8px 10px",
                    }}
                  >
                    <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                      Marketplace progress
                    </div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                      {progressSteps.map((label, idx) => (
                        <div
                          key={label}
                          style={{
                            height: 4,
                            flex: 1,
                            borderRadius: 999,
                            background:
                              idx <= currentStepIndex
                                ? "rgba(16,185,129,0.9)"
                                : "rgba(255,255,255,0.18)",
                          }}
                        />
                      ))}
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.6 }}>
                      {progressSteps.join(" | ")}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>
                      {nextLabel}
                    </div>
                  </div>
                )}
                {l.description && (
                  <p
                    style={{
                      margin: 0,
                      opacity: 0.95,
                      fontSize: 13,
                    }}
                  >
                    {l.description}
                  </p>
                )}

                {visibleOffers && visibleOffers.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleOpenListingOffers(l)}
                    className="mt-2 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-300 text-xs border border-amber-500/40 hover:bg-amber-500/30 transition-colors"
                  >
                    {visibleOffers.length === 1
                      ? "1 offer received"
                      : `${visibleOffers.length} offers received`}
                  </button>
                )}

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <Button
                    variant="soft"
                    onClick={() => {
                      if (isSoldVisible) return;
                      setChatFor({
                        id: l.id,
                        title: l.title,
                      });
                      setChatOpen(true);
                    }}
                    disabled={isSoldVisible}
                  >
                    Message
                  </Button>

                  {!isSeller && status === SHOP_STATUSES.LISTED && !isSoldVisible && (
                    <Button variant="soft" onClick={handleMakeOffer}>
                      Make offer
                    </Button>
                  )}

                  {!isSeller && status === SHOP_STATUSES.OFFER_ACCEPTED && !isMarketplaceOffer && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Button
                        variant="primary"
                        onClick={isPaidHeld ? undefined : handlePayNow}
                        disabled={isPaidHeld || isPaying || paymentLoadingId === l.id}
                      >
                        {isPaidHeld
                          ? "Payment held — awaiting seller"
                          : paymentLoadingId === l.id
                          ? "Processing payment."
                          : "Pay now"}
                      </Button>
                    </div>
                  )}
                  {!isSeller && status === SHOP_STATUSES.OFFER_ACCEPTED && isMarketplaceOffer && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Button variant="primary" disabled>
                        Pay now
                      </Button>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        Buyer completes payment from their offers page.
                      </span>
                    </div>
                  )}

                  {!isSeller &&
                    activeOffer &&
                    activeOffer.status === "handed_over" &&
                    isPaidHeld && (
                      <>
                        <Button
                          variant="primary"
                          onClick={handleBuyerConfirmReceived}
                        >
                          Received &amp; Approved
                        </Button>
                        <Button variant="soft" onClick={handleBuyerReportIssue}>
                          Report issue
                        </Button>
                      </>
                    )}
                  {!isSeller &&
                    activeOffer &&
                    activeOffer.status === "handed_over" &&
                    !isPaidHeld && (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Waiting for payment to be held before confirmation.
                      </div>
                    )}

                  {isSeller &&
                    activeOffer &&
                    activeOffer.status === "pending" && (
                      <>
                        <Button
                          variant="primary"
                          onClick={handleAcceptOffer}
                        >
                          Accept offer
                        </Button>
                        <Button variant="soft" onClick={handleRejectOffer}>
                          Reject
                        </Button>
                      </>
                    )}

                  {isSeller &&
                    activeOffer &&
                    activeOffer.status === "accepted" &&
                    isPaidHeld && (
                      <Button variant="soft" onClick={handleMarkHandedOver}>
                        Mark handed over
                      </Button>
                    )}
                  {isSeller &&
                    activeOffer &&
                    activeOffer.status === "accepted" &&
                    !isPaidHeld && (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Waiting for buyer payment (payment must be held before fulfilment).
                      </div>
                    )}

                  {status === SHOP_STATUSES.COMPLETED && (
                    <span
                      style={{
                        fontSize: 12,
                        opacity: 0.8,
                      }}
                    >
                      Payout (simulated): 95% to seller, 5% to Mudo.
                    </span>
                  )}

                  <Button variant="soft" onClick={() => toggleFav(l.id)}>
                    {isFav ? "✓ Saved" : "☆ Save"}
                  </Button>
                  <Button variant="soft" onClick={() => reportListing(l)}>
                    Report
                  </Button>

                  {mine && l.status !== "Sold" && (
                    <Button
                      variant="soft"
                      onClick={() => {
                        setEdit(l);
                        setModalOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  {mine && l.status !== "Sold" && (
                    <Button
                      variant={l.isPublished ? "soft" : "primary"}
                      className={
                        l.isPublished
                          ? "border border-amber-300/60 text-amber-200"
                          : ""
                      }
                      onClick={() => togglePublish(l.id, l.isPublished)}
                    >
                      {l.isPublished ? "Unpublish" : "Publish"}
                    </Button>
                  )}
                  {mine && l.status !== "Sold" && (
                    <Button variant="soft" onClick={() => markSold(l.id)}>
                      Mark as sold
                    </Button>
                  )}
                  {mine && (
                    <Button variant="danger" onClick={() => removeListing(l.id)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ListingOffersModal
        open={offersModalOpen}
        listing={selectedListing}
        offers={selectedOffers}
        updatingOfferId={updatingOfferId}
        onStatusChange={handleOfferStatusChange}
        onClose={() => setOffersModalOpen(false)}
      />

      <OfferModal
        open={!!offerModalFor}
        onClose={() => setOfferModalFor(null)}
        onSubmit={handleOfferModalSubmit}
        defaultAmount={
          offerModalFor?.price ? Number(offerModalFor.price) : undefined
        }
      />

        <ShopPayNowModal
          open={payModal.open}
          clientSecret={payModal.clientSecret}
          returnTo={payModal.returnTo}
          onClose={() => setPayModal({ open: false, clientSecret: null, returnTo: null })}
        />

      <ListingModal
        open={modalOpen}
        initial={edit}
        onClose={() => {
          setEdit(null);
          setModalOpen(false);
        }}
        onSave={upsertListing}
      />

      <ChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        listingId={chatFor?.id}
        listingTitle={chatFor?.title || ""}
        meId={me}
        onNotify={addNotification}
      />

      <OfferDrawer
        open={offerOpen}
        onClose={() => setOfferOpen(false)}
        listingId={offerFor?.id}
        listingTitle={offerFor?.title || ""}
        listing={offerFor}
        mine={offerFor?.mine}
        meId={me}
        pricingSettings={pricingSettings}
      />

      {/* Inbox */}
      <InboxModal
        isOpen={inboxOpen}
        initialThreadId={initialThreadId}
        onClose={() => {
          setInboxOpen(false);
          loadUnreadCount();
        }}
      />
    </PageLayout>
  );
}
